import React, { useEffect, useState } from 'react';
import { dashboardApi } from '../api/dashboard';
import { TrendingUp, TrendingDown, ShoppingCart, Package } from 'lucide-react';

const colorMap: Record<string,string> = { green:'badge-green', amber:'badge-amber', gray:'badge-gray', red:'badge-red' };

// v1.0.1
const Dashboard: React.FC = () => {
  const [data, setData]     = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    dashboardApi.get().then(res => { setData(res.data.data); setLoading(false); });
  }, []);

  if (loading) return <div className="empty-state"><p>Loading dashboard...</p></div>;
  if (!data) return null;

  const { kpis, recent_sales, recent_grns, low_stock, monthly_sales } = data;

  return (
    <>
      <div className="page-header">
        <h1 className="page-title">Dashboard</h1>
        <div style={{ fontSize:11, color:'var(--tx3)' }}>{new Date().toLocaleDateString('en-US', { weekday:'long', year:'numeric', month:'long', day:'numeric' })}</div>
      </div>

      <div className="kpi-grid" style={{ marginBottom:16 }}>
        <div className="kpi-card">
          <div className="kpi-label">Sales Today</div>
          <div className="kpi-value" style={{ color:'var(--green)' }}>{Number(kpis.sales_today).toFixed(2)}</div>
          <div className="kpi-sub">This month: {Number(kpis.sales_this_month).toFixed(2)}</div>
        </div>
        <div className="kpi-card">
          <div className="kpi-label">Gross Profit (Month)</div>
          <div className="kpi-value" style={{ color: kpis.profit_this_month >= 0 ? 'var(--green)' : 'var(--red)' }}>{Number(kpis.profit_this_month).toFixed(2)}</div>
          <div className="kpi-sub">Margin: {kpis.sales_this_month > 0 ? ((kpis.profit_this_month/kpis.sales_this_month)*100).toFixed(1) : '0'}%</div>
        </div>
        <div className="kpi-card">
          <div className="kpi-label">Cash Balance</div>
          <div className="kpi-value" style={{ color:'var(--blue)' }}>{Number(kpis.cash_balance).toFixed(2)}</div>
          <div className="kpi-sub">Account 1000</div>
        </div>
        <div className="kpi-card">
          <div className="kpi-label">Inventory Value</div>
          <div className="kpi-value" style={{ fontSize:18, color:'var(--tx)' }}>{Number(kpis.inventory_value).toFixed(2)}</div>
          <div className="kpi-sub">At cost price</div>
        </div>
      </div>

      <div className="kpi-grid" style={{ marginBottom:16 }}>
        <div className="kpi-card">
          <div className="kpi-label">Accounts Receivable</div>
          <div className="kpi-value" style={{ fontSize:18, color:'var(--blue)' }}>{Number(kpis.total_ar).toFixed(2)}</div>
          <div className="kpi-sub">Outstanding from customers</div>
        </div>
        <div className="kpi-card">
          <div className="kpi-label">Accounts Payable</div>
          <div className="kpi-value" style={{ fontSize:18, color:'var(--red)' }}>{Number(kpis.total_ap).toFixed(2)}</div>
          <div className="kpi-sub">Owed to suppliers</div>
        </div>
        {low_stock.length > 0 && (
          <div className="kpi-card" style={{ borderColor:'var(--red)' }}>
            <div className="kpi-label" style={{ color:'var(--red)' }}>Low Stock Alert</div>
            <div className="kpi-value" style={{ fontSize:24, color:'var(--red)' }}>{low_stock.length}</div>
            <div className="kpi-sub">Items below reorder level</div>
          </div>
        )}
      </div>

      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12, marginBottom:12 }}>
        <div className="card">
          <div className="card-header"><span><ShoppingCart size={13} style={{marginRight:6}} />Recent Sales</span></div>
          <table>
            <thead><tr><th>Sale No</th><th>Customer</th><th style={{textAlign:'right'}}>Amount</th><th style={{textAlign:'right'}}>Profit</th></tr></thead>
            <tbody>
              {recent_sales.length === 0 ? (
                <tr><td colSpan={4}><div className="empty-state" style={{padding:20}}><p>No sales yet.</p></div></td></tr>
              ) : recent_sales.map((s: any, i: number) => (
                <tr key={i}>
                  <td className="td-code">{s.sale_no}</td>
                  <td style={{fontSize:11,color:'var(--tx2)'}}>{s.customer_name || 'Walk-in'}</td>
                  <td className="td-num td-green">{Number(s.total_amount).toFixed(2)}</td>
                  <td className="td-num" style={{color:s.gross_profit>=0?'var(--green)':'var(--red)',fontSize:11}}>{Number(s.gross_profit).toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="card">
          <div className="card-header"><span><Package size={13} style={{marginRight:6}} />Recent GRNs</span></div>
          <table>
            <thead><tr><th>GRN No</th><th>Supplier</th><th>Status</th><th>Date</th></tr></thead>
            <tbody>
              {recent_grns.length === 0 ? (
                <tr><td colSpan={4}><div className="empty-state" style={{padding:20}}><p>No GRNs yet.</p></div></td></tr>
              ) : recent_grns.map((g: any, i: number) => (
                <tr key={i}>
                  <td className="td-code">{g.grn_no}</td>
                  <td style={{fontSize:11,color:'var(--tx2)'}}>{g.supplier_name}</td>
                  <td><span className={`badge ${colorMap[g.status_color]||'badge-gray'}`}>{g.status}</span></td>
                  <td style={{fontSize:11,color:'var(--tx3)'}}>{new Date(g.created_at).toLocaleDateString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {monthly_sales.length > 0 && (
        <div className="card">
          <div className="card-header"><span><TrendingUp size={13} style={{marginRight:6}} />Monthly Sales</span></div>
          <div className="card-body">
            <div style={{ display:'flex', gap:8, alignItems:'flex-end', height:120 }}>
              {monthly_sales.map((m: any, i: number) => {
                const maxTotal = Math.max(...monthly_sales.map((x: any) => Number(x.total)));
                const height   = maxTotal > 0 ? (Number(m.total) / maxTotal) * 100 : 0;
                return (
                  <div key={i} style={{ flex:1, display:'flex', flexDirection:'column', alignItems:'center', gap:4 }}>
                    <div style={{ fontSize:10, color:'var(--tx3)', fontFamily:'monospace' }}>{Number(m.total).toFixed(0)}</div>
                    <div style={{ width:'100%', background:'var(--blue)', borderRadius:'4px 4px 0 0', height:`${height}%`, minHeight:4, transition:'height .3s' }} title={`${m.month}: ${m.total}`} />
                    <div style={{ fontSize:10, color:'var(--tx3)' }}>{m.month.slice(5)}</div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {low_stock.length > 0 && (
        <div className="card" style={{ marginTop:12 }}>
          <div className="card-header" style={{ color:'var(--red)' }}><span>Low Stock Items</span></div>
          <table>
            <thead><tr><th>SKU</th><th>Product</th><th style={{textAlign:'right'}}>On Hand</th><th style={{textAlign:'right'}}>Min Level</th></tr></thead>
            <tbody>
              {low_stock.map((item: any, i: number) => (
                <tr key={i}>
                  <td className="td-code">{item.sku}</td>
                  <td>{item.name}</td>
                  <td className="td-num td-red">{Number(item.qty_on_hand).toFixed(2)}</td>
                  <td className="td-num">{Number(item.min_reorder_level).toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
};

export default Dashboard;



